/* Jediné místo, kde strana poradce sahá na klientské formuláře.
 *
 * PROČ ADAPTÉR A NE api.get() rozházené po komponentách:
 *  - Kontrakt s backendem je na jednom místě. Když se routa přejmenuje,
 *    mění se jeden soubor, ne pět.
 *  - Plné odpovědi a odhalení citlivého údaje jsou VLASTNÍ endpointy.
 *    U zdravotního dotazníku je `nactiPlne` dokonce jediná cesta k obsahu —
 *    server ho v běžném detailu nevrací vůbec a každé otevření si zapíše do
 *    auditu. Kdyby se obsah vozil v seznamu, byl by v paměti prohlížeče
 *    i v devtools pokaždé, když poradce otevře složku klienta.
 *
 * NIC TU NEODESÍLÁ ZPRÁVY. Neexistuje funkce „poslat klientovi“ — odkaz se
 * jen vyrobí, poradce si ho zkopíruje a pošle sám. Je to záměr, ne opomenutí:
 * kdyby výzvu k vyplnění zdravotního dotazníku rozesílal server, stačila by
 * jedna překlepnutá adresa u klienta a citlivá výzva jde cizímu člověku.
 */
import api from "@/lib/api";

const ZAKLAD = "/api/formulare";

/** Nabídka typů, které jde klientovi poslat. */
export async function nactiTypy(jazyk) {
  const { data } = await api.get(`${ZAKLAD}/typy`, { params: { jazyk } });
  const typy = Array.isArray(data) ? data : data?.typy;
  if (!Array.isArray(typy)) throw new Error("neocekavana-odpoved");
  return typy.filter(Boolean);
}

/** Formuláře klienta — jen stavy a platnost odkazů, ŽÁDNÉ odpovědi. */
export async function nactiFormulare(contactId) {
  const { data } = await api.get(ZAKLAD, { params: { contact_id: contactId } });
  const polozky = Array.isArray(data) ? data : data?.polozky;
  /* Ne-pole NIKDY neprojde dál jako prázdný seznam. Poradce by to četl jako
     „nic neposláno“, vyrobil druhý odkaz — a klient, který zrovna vyplňuje
     ten první, by začínal od začátku. */
  if (!Array.isArray(polozky)) throw new Error("neocekavana-odpoved");
  return polozky.filter(Boolean);
}

/**
 * Vyrobí nový odkaz. Vrací { id, typ, url_path, plati_do }.
 * `url_path` skládá server (obsahuje token) — frontend si adresu NIKDY
 * neskládá sám z id klienta, jinak by šlo odkaz uhodnout.
 */
export async function vytvorOdkaz({ contactId, typ, platnostDnu, jazyk }) {
  const { data } = await api.post(ZAKLAD, {
    contact_id: contactId,
    typ,
    platnost_dnu: platnostDnu,
    jazyk,
  });
  return data;
}

/** Zneplatní odkaz — po tomhle se formulář klientovi už neotevře. */
export async function zneplatniOdkaz(id) {
  const { data } = await api.post(`${ZAKLAD}/zaznam/${id}/zneplatnit`);
  return data;
}

/** Definice + stav. U zvláštní kategorie BEZ odpovědí — ty jen přes nactiPlne. */
export async function nactiDetail(id, jazyk) {
  const { data } = await api.get(`${ZAKLAD}/zaznam/${id}`, { params: { jazyk } });
  return data;
}

/**
 * Plné odpovědi. Vědomý krok poradce → server si ho zapíše do auditu.
 * U zdravotního dotazníku je to jediná cesta, jak se k obsahu dostat.
 */
export async function nactiPlne(id, jazyk) {
  const { data } = await api.get(`${ZAKLAD}/zaznam/${id}/plny`, { params: { jazyk } });
  return data;
}

/**
 * Označí odeslaný formulář „Příjmy a výdaje“ jako převzatý do analýzy.
 * Čísla do analýzy zapisuje stránka analýzy sama (finmap v2, autosave) —
 * tohle je jen razítko, aby banner zmizel a složka ukázala „převzato“.
 */
export async function oznacPrevzato(id) {
  const { data } = await api.post(`${ZAKLAD}/zaznam/${id}/prevzato`);
  return data;
}

/** Odhalí JEDNU citlivou hodnotu (rodné číslo, číslo účtu). Taky do auditu. */
export async function odhalPole(id, poleId) {
  const { data } = await api.get(`${ZAKLAD}/zaznam/${id}/odhalit/${encodeURIComponent(poleId)}`);
  return data;
}
